/**
 * Blueprint review — five tabs, one document.
 *
 * Tab order follows the product's own flow (00-product-brief.md flows 4-7): what changed,
 * the structure itself, the platform dry run, the push, the results. The Changes tab is
 * the default for any version above the first, because "what exactly changed before I
 * push" is the question the screen exists to answer.
 */
import { useMemo, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useAdAccounts, useBlueprints } from '@/api/hooks'
import type { PreflightReport, PushState, ValidationReport } from '@/api/types'
import { STATE_COPY } from '@/lib/pushState'
import { formatDateTime } from '@/lib/format'
import { Badge, Button, Cap, ErrorNotice, Select, Spinner, type Tone } from '@/components/ui'
import { DiffPanel } from './DiffPanel'
import { StructurePanel } from './StructurePanel'
import { DryRunPanel } from './DryRunPanel'
import { PushPanel } from './PushPanel'
import { MetricsPanel } from '@/features/metrics/MetricsPanel'

export type BlueprintTab = 'changes' | 'structure' | 'dry-run' | 'push' | 'metrics'

const TAB_LABELS: Array<[BlueprintTab, string]> = [
  ['changes', 'Changes'],
  ['structure', 'Structure and edits'],
  ['dry-run', 'Dry run'],
  ['push', 'Push'],
  ['metrics', 'Metrics'],
]

const STATE_TONE: Record<PushState, Tone> = {
  draft: 'neutral',
  preflight: 'forge',
  blocked: 'danger',
  dry_run: 'forge',
  needs_fix: 'warn',
  queued_for_quota: 'warn',
  publishing_pages: 'forge',
  pushing: 'forge',
  reversing: 'forge',
  reversed: 'neutral',
  live_paused: 'ok',
  failed: 'danger',
}

export function BlueprintScreen({
  projectId,
  blueprintId,
  tab,
  against,
  onNavigateTab,
}: {
  projectId: string
  blueprintId: string
  tab: BlueprintTab
  against?: string
  onNavigateTab: (tab: BlueprintTab) => void
}) {
  const navigate = useNavigate()
  const versions = useBlueprints(projectId)
  const accounts = useAdAccounts()

  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null)
  const [dryRuns, setDryRuns] = useState<Record<string, ValidationReport>>({})
  const [preflights, setPreflights] = useState<Record<string, PreflightReport>>({})
  const [highlightPath, setHighlightPath] = useState<string | null>(null)

  const ordered = useMemo(
    () => [...(versions.data ?? [])].sort((a, b) => a.version - b.version),
    [versions.data],
  )
  const current = ordered.find((summary) => summary.id === blueprintId) ?? null
  const index = ordered.findIndex((summary) => summary.id === blueprintId)
  const previous = index > 0 ? ordered[index - 1]! : null
  const accountList = accounts.data?.accounts ?? []

  function onTabKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, current: BlueprintTab) {
    const currentIndex = TAB_LABELS.findIndex(([key]) => key === current)
    let nextIndex: number | null = null
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % TAB_LABELS.length
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + TAB_LABELS.length) % TAB_LABELS.length
    if (event.key === 'Home') nextIndex = 0
    if (event.key === 'End') nextIndex = TAB_LABELS.length - 1
    if (nextIndex === null) return
    event.preventDefault()
    const next = TAB_LABELS[nextIndex]!
    onNavigateTab(next[0])
    requestAnimationFrame(() => document.getElementById(`tab-${next[0]}`)?.focus())
  }

  if (versions.isLoading) return <Spinner label="Loading versions…" />
  if (versions.isError) return <ErrorNotice error={versions.error} />
  if (!current) {
    return (
      <div className="flex flex-col gap-3">
        <ErrorNotice error={new Error(`Blueprint ${blueprintId} is not part of project ${projectId}.`)} />
        <Link to="/projects/$projectId" params={{ projectId }} className="text-[12.5px] text-forge-300">
          ← Back to the project
        </Link>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-[12px] text-mist-400">
            <Link
              to="/projects/$projectId"
              params={{ projectId }}
              className="underline decoration-dotted hover:text-mist-200"
            >
              Project
            </Link>
            <span aria-hidden="true">/</span>
            <span className="text-mist-300">Blueprint review</span>
          </nav>
          <h1 className="mt-1 flex flex-wrap items-center gap-2.5 text-[22px] leading-8 font-semibold tracking-tight text-mist-100">
            Version {current.version}
            <Badge tone={STATE_TONE[current.push_state]} title={STATE_COPY[current.push_state].detail}>
              {STATE_COPY[current.push_state].label}
            </Badge>
            {current.policy_lint_passed === false ? (
              <Badge tone="danger">policy linter found violations</Badge>
            ) : null}
          </h1>
          <p className="num mt-0.5 text-[12px] text-mist-500">
            {current.id} · created {formatDateTime(current.created_at)} · {current.campaign_count} campaign
            · {current.ad_group_count} ad groups · {current.keyword_count} keywords · {current.ad_count} ads
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="version-switch" className="label-cap">
              Version
            </label>
            <Select
              id="version-switch"
              data-testid="version-switch"
              value={blueprintId}
              onChange={(event) =>
                void navigate({
                  to: '/projects/$projectId/blueprints/$blueprintId',
                  params: { projectId, blueprintId: event.target.value },
                  search: { tab: 'changes' },
                })
              }
              className="w-[150px] py-1 text-[12px]"
            >
              {ordered.map((summary) => (
                <option key={summary.id} value={summary.id}>
                  v{summary.version} — {summary.ad_group_count} groups
                </option>
              ))}
            </Select>
          </div>
          <Button
            variant="ghost"
            data-testid="back-to-project"
            onClick={() => void navigate({ to: '/projects/$projectId', params: { projectId } })}
          >
            ← All versions
          </Button>
        </div>
      </div>

      <div role="tablist" aria-label="Blueprint review sections" className="flex flex-wrap gap-1 border-b border-ink-750">
        {TAB_LABELS.map(([key, label]) => {
          const selected = key === tab
          return (
            <button
              key={key}
              id={`tab-${key}`}
              role="tab"
              type="button"
              aria-selected={selected}
              aria-controls={`panel-${key}`}
              tabIndex={selected ? 0 : -1}
              data-testid={`tab-${key}`}
              onClick={() => onNavigateTab(key)}
              onKeyDown={(event) => onTabKeyDown(event, key)}
              className={`-mb-px rounded-t-md border-b-2 px-3.5 py-2 text-[13px] transition-colors ${
                selected
                  ? 'border-forge-400 text-mist-100'
                  : 'border-transparent text-mist-400 hover:border-ink-600 hover:text-mist-200'
              }`}
            >
              {label}
              {key === 'changes' && previous ? (
                <span className="num ml-2 text-[11px] text-mist-500">
                  v{previous.version}→v{current.version}
                </span>
              ) : null}
            </button>
          )
        })}
      </div>

      <div
        id={`panel-${tab}`}
        role="tabpanel"
        aria-labelledby={`tab-${tab}`}
        tabIndex={0}
        className="outline-none"
      >
        {tab === 'changes' ? (
          previous ? (
            <DiffPanel blueprintId={current.id} against={against ?? previous.id} />
          ) : (
            <div className="flex flex-col gap-3">
              <p className="text-[13px] text-mist-300" data-testid="first-version-note">
                This is the first version of the Blueprint, so there is nothing to diff against yet. Edit an
                asset on the Structure tab (or generate another version) and the change set appears here.
              </p>
              <Button variant="secondary" onClick={() => onNavigateTab('structure')}>
                Go to structure and edits
              </Button>
            </div>
          )
        ) : null}

        {tab === 'structure' ? (
          <StructurePanel
            blueprintId={current.id}
            highlightPath={highlightPath}
            onSaved={(summary) => {
              setHighlightPath(null)
              void navigate({
                to: '/projects/$projectId/blueprints/$blueprintId',
                params: { projectId, blueprintId: summary.id },
                search: { tab: 'changes' },
              })
            }}
          />
        ) : null}

        {tab === 'dry-run' ? (
          <DryRunPanel
            blueprintId={current.id}
            accounts={accountList}
            selectedAccountId={selectedAccountId}
            onSelectAccount={setSelectedAccountId}
            report={selectedAccountId ? (dryRuns[selectedAccountId] ?? null) : null}
            onReport={(accountId, report) => setDryRuns((prev) => ({ ...prev, [accountId]: report }))}
            onInspectPath={(path) => {
              setHighlightPath(path)
              onNavigateTab('structure')
            }}
          />
        ) : null}

        {tab === 'push' ? (
          <PushPanel
            blueprintId={current.id}
            version={current.version}
            accounts={accountList}
            selectedAccountId={selectedAccountId}
            onSelectAccount={setSelectedAccountId}
            dryRuns={dryRuns}
            preflights={preflights}
            onPreflight={(accountId, report) => setPreflights((prev) => ({ ...prev, [accountId]: report }))}
            onGoToDryRun={() => onNavigateTab('dry-run')}
          />
        ) : null}

        {tab === 'metrics' ? <MetricsPanel projectId={projectId} /> : null}
      </div>

      {highlightPath ? (
        <p className="text-[11.5px] text-forge-300" data-testid="highlight-active">
          Highlighting <code className="num">{highlightPath}</code> in the structure tree.
        </p>
      ) : null}
      {accounts.isError ? <ErrorNotice error={accounts.error} /> : null}
      <Cap className="text-mist-500">
        Contract: every payload on this screen is a generated type from openapi.json.
      </Cap>
    </div>
  )
}
